import { useEffect, useState } from 'react';
import { api, StudyPlan as Plan, StudyPlanTopic } from '../api';
import { Icon } from '../Icon';
import { FurtherReading } from '../FurtherReading';

function pct(n: number) { return `${Math.round(n * 100)}%`; }
const letters = (keys: string[]) => keys.join(', ').toUpperCase();

/**
 * What to work on next, from the learner's own answers.
 *
 * Every figure is a raw count with its denominator, and the page says what it
 * is not (Principle VII). There is deliberately no "ready" line, no target date
 * and no estimate: a practice bank cannot tell anyone how an exam will go.
 */
export function StudyPlan({ onPractice, onBack }: {
  onPractice: (packId: string, bankVersionId: string, topicId: string) => void;
  onBack: () => void;
}) {
  const [plan, setPlan] = useState<Plan | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<Plan>('/me/study-plan').then(setPlan).catch((e) => setError(e.message));
  }, []);

  if (error) return <div className="notice notice--warn" role="alert">{error}</div>;
  if (!plan) return <p>Building your study plan…</p>;

  return (
    <>
      <button className="btn-ghost btn-back" onClick={onBack}><Icon name="arrow-left" />Back to banks</button>
      <h1>Your study plan</h1>
      <p className="muted">{plan.note}</p>

      {plan.subjects.length === 0 && (
        <div className="notice">
          Your plan appears here once you have finished a session. Each topic you answer is listed
          with how many you got right, the questions you still have wrong, and reading on each one.
          <div className="btn-row" style={{ marginTop: '0.75rem' }}>
            <button className="btn-primary" onClick={onBack}><Icon name="play" />Start a session</button>
          </div>
        </div>
      )}

      {plan.subjects.map((s) => (
        <section key={s.packId} aria-labelledby={`plan-${s.packId}`}>
          <h2 id={`plan-${s.packId}`}>{s.subject}</h2>
          <p className="muted">
            {s.correct} of {s.answered} answers correct across your sessions ({pct(s.answered ? s.correct / s.answered : 0)}).
            Topics are ordered by your accuracy, lowest first.
          </p>
          {s.disclosure && (
            <p className="muted">
              {s.disclosure.bankEdition} · {s.disclosure.contentOwner} · published{' '}
              {new Date(s.disclosure.publishedAt).toLocaleDateString()} · {s.disclosure.independenceStatement}
            </p>
          )}
          {!s.bankVersionId && (
            <p className="notice">No published bank is available for this subject, so practice cannot start from here.</p>
          )}

          {s.ranked.length > 0 && (
            <ol className="plan-topics">
              {s.ranked.map((t, i) => (
                <li key={t.topicId}>
                  <TopicCard topic={t} position={i + 1} canPractice={Boolean(s.bankVersionId)}
                             onPractice={() => s.bankVersionId && onPractice(s.packId, s.bankVersionId, t.topicId)} />
                </li>
              ))}
            </ol>
          )}

          {s.emerging.length > 0 && (
            <>
              <h3>Not enough answers yet</h3>
              <p className="muted">
                Fewer than three answers in these topics, so they are not ranked — one or two
                questions say little about a topic.
              </p>
              <ul className="plan-topics">
                {s.emerging.map((t) => (
                  <li key={t.topicId}>
                    <TopicCard topic={t} headingLevel={4} canPractice={Boolean(s.bankVersionId)}
                               onPractice={() => s.bankVersionId && onPractice(s.packId, s.bankVersionId, t.topicId)} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      ))}
    </>
  );
}

function TopicCard({ topic: t, position, headingLevel = 3, canPractice, onPractice }: {
  topic: StudyPlanTopic; position?: number; headingLevel?: 3 | 4; canPractice: boolean; onPractice: () => void;
}) {
  const headingId = `topic-${t.topicId}`;
  // Under "Not enough answers yet" the cards sit one level deeper, so the
  // outline a screen reader announces matches the page.
  const Heading = headingLevel === 4 ? 'h4' : 'h3';
  return (
    <article className="card" aria-labelledby={headingId}>
      <Heading id={headingId} style={{ marginTop: 0 }}>
        {position !== undefined && <span className="muted">{position}. </span>}
        {t.topicName}{' '}
        {/* Words, not just colour (Principle V). */}
        {t.needsWork
          ? <span className="pill pill--bad">Below 70% correct</span>
          : t.answered >= 3 && <span className="pill pill--ok">70% or more correct</span>}
      </Heading>
      <p style={{ margin: '0 0 0.5rem' }}>
        <strong>{t.correct} of {t.answered}</strong> correct ({pct(t.accuracy)}) · {t.incorrect} wrong
      </p>
      <div className="bar-meter" aria-hidden="true"><span style={{ width: pct(t.accuracy) }} /></div>

      <div className="btn-row" style={{ marginTop: '0.75rem' }}>
        <button className="btn-primary" disabled={!canPractice} onClick={onPractice}>
          <Icon name="play" />Practice this topic
          <span className="visually-hidden">: {t.topicName}</span>
        </button>
      </div>

      {t.stillWrongCount > 0 ? (
        <details className="plan-disclosure" style={{ marginTop: '0.75rem' }}>
          <summary style={{ cursor: 'pointer', minHeight: '2.75rem', display: 'flex', alignItems: 'center' }}>
            Show the {t.stillWrongCount} question{t.stillWrongCount === 1 ? '' : 's'} still wrong
            {t.stillWrong.length < t.stillWrongCount && ` (showing the ${t.stillWrong.length} most recent)`}
          </summary>
          {t.stillWrong.map((q) => {
            const text = (k: string | undefined) => q.options.find((o) => o.key === k)?.text ?? '';
            return (
              <div key={q.questionId} className="plan-question">
                <p style={{ marginTop: 0 }}>{q.stem}</p>
                <p>
                  You answered <strong>{letters(q.selectedResponse)}</strong>
                  {q.selectedResponse.length === 1 && ` (${text(q.selectedResponse[0])})`} ·{' '}
                  correct answer <strong>{letters(q.correctResponse)}</strong>
                  {q.correctResponse.length === 1 && ` (${text(q.correctResponse[0])})`}
                </p>
                {q.rationale && <p className="muted">{q.rationale}</p>}
                <FurtherReading links={q.furtherReading} id={q.questionId} />
              </div>
            );
          })}
        </details>
      ) : (
        <p className="muted" style={{ marginBottom: 0 }}>
          {t.answered ? 'Nothing still wrong here — your latest answer to each question was right.' : ''}
        </p>
      )}
    </article>
  );
}
